import { message } from 'antd';
import axios, { AxiosInstance } from 'axios';
import { isArray, isEmpty } from 'underscore';
import { IError } from '../types/api';
import Auth from './endpoints/Auth';
import DataPreview from './endpoints/DataPreview';
import Pages from './endpoints/Pages';
import Search from './endpoints/Search';
import SearchDetail from './endpoints/SearchDetail';
import Service from './endpoints/Service';
import Price from './endpoints/Price';
import ShoppingCart from './endpoints/Shopping';
import DownloadRequest from './endpoints/DownloadRequest';
import FileEndPoint from './endpoints/FileEndPoint';
import ChartRequest from './endpoints/Chart';
import BSApi from './endpoints/BSApi';
import PreCheckout from './endpoints/PreCheckout';
import MoodV2Order from './endpoints/MoodV2Order';
import FWEPoint from './endpoints/FWEPoint';
import Metadata from './endpoints/Metadata';
import Notifications from './endpoints/Notifications';
import CurrentUser from './endpoints/CurrentUser';
import { IConfig } from '../types/config';
import getConfig from './config';
import InsituPoint from './endpoints/insituPoint';

export const initApis = async () => {
  if (!Api.SERVER_DOMAIN) {
    const json: IConfig = await getConfig();
    if (json.serverDomain) {
      Api.init(
        json.serverDomain,
        json.iisWebServerSiteBackend,
        json.urlVersion1,
        json.meshDomain,
        json.cloudAdminUrl,
        json.fweUrl,
        json?.useNewSpectraEndpoint === 'true',
        json.signalRServer,
        json.dhiSupportUrl
      );
    }
    Api.configData = json;
    return json;
  }
};
export let axiosInstance: AxiosInstance;

export default class Api {
  static SERVER_DOMAIN = '';
  static MESH_DOMAIN = '';
  static IISSite = '';
  static VERSION1_URL = '';
  static CLOUD_ADMIN_URL = '';
  static FWE_URL = '';
  static UseNewSpectraEndpoint = false;
  static SignalRServer = '';
  static DhiSupportUrl = '';
  static XSRF_TOKEN = '';
  static configData: IConfig | null = null;

  static init(
    serverDomain: string,
    iisWebServerSiteBackend: string,
    urlVersion1: string,
    meshDomain: string,
    cloudAdminUrl: string,
    fweUrl: string,
    useNewSpectraEndpoint: boolean,
    signalRServer: string,
    dhiSupportUrl: string
  ) {
    axios.defaults.baseURL = serverDomain + iisWebServerSiteBackend + '/api';
    axios.defaults.headers.post['Content-Type'] = 'application/json';
    Api.SERVER_DOMAIN = serverDomain;
    Api.MESH_DOMAIN = meshDomain ?? serverDomain;
    Api.VERSION1_URL = urlVersion1;
    Api.IISSite = iisWebServerSiteBackend;
    Api.CLOUD_ADMIN_URL = cloudAdminUrl;
    Api.FWE_URL = fweUrl;
    Api.UseNewSpectraEndpoint = useNewSpectraEndpoint;
    Api.SignalRServer = signalRServer;
    Api.DhiSupportUrl = dhiSupportUrl;

    axiosInstance = axios.create();

    axiosInstance.interceptors.response.use(
      (response) => {
        if (isArray(response.data) || response.data) {
          return response;
        }

        if (response.data.Error === null) {
          return response;
        } else {
          const knownError: IError = response.data.Error as IError;
          // errors with code 1 are expected errors and will be handled in the calling component
          if (!isEmpty(knownError) && knownError.Code === 1) {
            return response;
          } else {
            return Promise.reject(response.data.Error);
          }
        }
      },
      (error) => {
        if (error.response?.status === 401) {
          window.dispatchEvent(new CustomEvent('mood:session-unauthorized'));
          return Promise.reject(error);
        }

        if (error.response) {
          const knownError: IError = error as IError;

          if (!isEmpty(knownError) && knownError.ErrorMessage) {
            console.error(knownError.ErrorMessage);
          } else if (error.message) {
            console.error(error.message);
          } else {
            console.error(JSON.stringify(error));
          }
        } else {
          console.error(JSON.stringify(error));
        }
        return Promise.reject(error);
      }
    );

    axiosInstance.interceptors.request.use(
      async (config) => {
        // Only add the token to mutating requests
        if (!['post'].includes(config.method?.toLowerCase() || ''))
          return config;

        if (!Api.XSRF_TOKEN) {
          // If we don't have a token yet, get one
          const token = await Api.auth.getAntiForgeryToken();

          if (token && !isEmpty(token)) {
            config.headers['X-XSRF-TOKEN'] = token.requestToken;
            Api.XSRF_TOKEN = token.requestToken;
          }
        } else {
          // We have a token, add it to the request
          config.headers['X-XSRF-TOKEN'] = Api.XSRF_TOKEN;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  static clearForgeryToken() {
    this.XSRF_TOKEN = '';
  }

  static detail = new SearchDetail();
  static service = new Service();
  static search = new Search();
  static auth = new Auth();
  static dataPreview = new DataPreview();
  static pages = new Pages();
  static price = new Price();
  static shopping = new ShoppingCart();
  static downloadRequest = new DownloadRequest();
  static fileEndPoint = new FileEndPoint();
  static chart = new ChartRequest();
  static bsApi = new BSApi();
  static preCheckout = new PreCheckout();
  static moodV2Order = new MoodV2Order();
  static FWEPoint = new FWEPoint();
  static insituPoint = new InsituPoint();
  static metadata = new Metadata();
  static notifications = new Notifications();
  static currentUser = new CurrentUser();
}

export function doError(error: string) {
  if (error.includes('Request aborted')) return;

  message.error(error);
}

export const DEFAULT_ERROR_MESSAGE =
  'Unexpected application exception. Please report an issue.';
